/**
 * FRONT-2410: the workspace's rule, shown under an app's own switch — what
 * the app owner may do here, why, and a way to the place it is decided.
 * Admins get a link to System Settings → Apps (a new tab, so an open Building
 * Session or app editor is never lost); everyone else is told to ask one.
 *
 * FRONT-2501: no "Show details" toggle. An allowed rule says nothing, unless
 * the workspace narrows the choice (`detail`, e.g. "5 of 12 agents allowed").
 * A blocked rule keeps its one visible line — a reason the owner must act on
 * cannot live only in a hover hint, which never shows on a touch screen — and
 * the longer explanation rides a tooltip on that line.
 */
import { LockSimpleIcon } from '@phosphor-icons/react';
import { Tip } from '~/components/ui/tip';
import { systemSettingsPath } from '~/lib/system-settings-sections';
import { useIsAdmin } from '~/hooks/queries/use-is-admin';
import { ruleAllows, type AppRuleKey, type AppWorkspacePolicy } from './app-policy-model';

export type WorkspaceRuleState = 'allowed' | 'blocked' | 'blocked-but-on';

/** Pure — unit tested. `enabled` is whether THIS app has it switched on. */
export function workspaceRuleState(policy: AppWorkspacePolicy, key: AppRuleKey, enabled: boolean): WorkspaceRuleState {
  if (ruleAllows(policy, key)) return 'allowed';
  return enabled ? 'blocked-but-on' : 'blocked';
}

const LINE: Record<Exclude<WorkspaceRuleState, 'allowed'>, string> = {
  blocked: 'Blocked by your workspace — nobody can turn it on for an app',
  'blocked-but-on': 'Blocked by your workspace — switched on here, but stopped until an admin allows it again',
};

const BLOCKED_HINT =
  'A workspace admin has turned this off for every app. The app keeps its own setting and picks up where it left off if it is allowed again.';

export function WorkspaceRuleNote({
  state,
  detail,
}: {
  state: WorkspaceRuleState;
  /** Only when the workspace narrows the choice, e.g. "5 of 12 agents allowed". */
  detail?: string;
}) {
  const { isAdmin } = useIsAdmin();
  if (state === 'allowed') {
    return detail ? <p className="mt-1.5 text-xs text-muted-foreground">Workspace: {detail}</p> : null;
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <Tip label={BLOCKED_HINT}>
        <span className="flex items-center gap-1 text-foreground">
          <LockSimpleIcon size={12} aria-hidden="true" />
          {LINE[state]}
        </span>
      </Tip>
      {isAdmin ? (
        <a
          href={systemSettingsPath('app-policy')}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary hover:underline"
        >
          Change in System Settings
        </a>
      ) : (
        <span className="text-muted-foreground">Only a workspace admin can change this.</span>
      )}
    </div>
  );
}
